import { ResponseError } from '@evimo/client/runtime';
import { buildApiUrl } from '~/app/lib/api-config';
import {
  assetApi,
  assetRequestInit,
} from '~/app/services/asset/asset-api-instances';
import { parseClientJsonResponse } from '~/app/services/http/parse-client-json-response';
import { z } from 'zod';

const importIssueSchema = z.object({
  message: z.string(),
  line: z.number().int(),
});

const importReportSchema = z.object({
  totalLines: z.number().int(),
  errors: z.array(importIssueSchema),
  warnings: z.array(importIssueSchema),
  durationMs: z.number().int().optional(),
  memoryBytes: z.number().int().optional(),
});

const uploadedFileSchema = z.object({
  id: z.string(),
  fileKey: z.string(),
  fileName: z.string(),
  fileMimeType: z.string(),
  fileSize: z.number().int(),
  createdAt: z.string(),
});

const importRequestStatusSchema = z.enum([
  'IN_PROGRESS',
  'SUCCEEDED',
  'FAILED',
]);

const importExecutionStatusSchema = z.enum([
  'IN_PROGRESS',
  'SUCCESS',
  'FAILED',
]);

const uploadAssetResponseSchema = z.object({
  status: importExecutionStatusSchema,
  totalAssets: z.number().int(),
  report: importReportSchema,
});

const importRequestItemSchema = z.object({
  id: z.string(),
  fileType: z.string(),
  status: importRequestStatusSchema,
  uploadedFileId: z.string().nullable(),
  totalAssets: z.number().int().nullable().optional(),
  report: importReportSchema.nullable().optional(),
  error: z.string().nullable().optional(),
  columnMapping: z.record(z.string(), z.string()).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  uploadedFile: uploadedFileSchema.nullable().optional(),
});

const importRequestsResponseSchema = z.object({
  items: z.array(importRequestItemSchema),
});

const extractHeadersResponseSchema = z.object({
  headers: z.array(z.string()),
});

const suggestMappingsResponseSchema = z.object({
  suggestions: z.record(z.string(), z.string()),
  availableFields: z.array(z.string()),
  requiredFields: z.array(z.string()),
});

const getMappingSuggestionResponseSchema = z.object({
  suggestions: z.record(z.string(), z.string()),
});

export type ImportRequestItem = z.infer<typeof importRequestItemSchema>;
export type ImportRequestsResponse = z.infer<
  typeof importRequestsResponseSchema
>;
export type UploadAssetResponse = z.infer<typeof uploadAssetResponseSchema>;

async function readAssetImportClientError(
  error: unknown,
  fallbackMessage: string,
): Promise<never> {
  if (error instanceof ResponseError) {
    const message = await error.response.text().catch(() => '');
    throw new Error(message || fallbackMessage);
  }
  throw error;
}

export type UploadAssetImportParams = {
  organizationId: string;
  file: File;
  assetTypeId: string;
  assetModelId?: string;
  suggestions?: Record<string, string>;
  headers?: string[];
  availableFields?: string[];
};

type ApiErrorPayload = {
  message?: string | string[];
  error?: string;
};

/**
 * Extract i18n key from error message for client-side error messages
 */
export const ERROR_KEYS = {
  PARSE_ERROR: 'errors.assetImport.parseError',
  NETWORK_ERROR: 'errors.assetImport.networkError',
  UPLOAD_ABORTED: 'errors.assetImport.uploadAborted',
} as const;

function extractErrorMessage(
  payload: ApiErrorPayload | null | undefined,
  fallback: string,
): string {
  if (!payload) {
    return fallback;
  }

  if (
    typeof payload.message === 'string' &&
    payload.message.trim().length > 0
  ) {
    return payload.message;
  }

  if (Array.isArray(payload.message) && payload.message.length > 0) {
    const first = payload.message.find(
      (item) => typeof item === 'string' && item.trim().length > 0,
    );
    if (first) {
      return first;
    }
  }

  if (typeof payload.error === 'string' && payload.error.trim().length > 0) {
    return payload.error;
  }

  return fallback;
}

async function safeErrorFromResponse(response: Response): Promise<string> {
  const fallback = response.statusText || 'Upload failed';

  try {
    const payload = (await response.json()) as ApiErrorPayload;
    return extractErrorMessage(payload, fallback);
  } catch {
    // Ignore parse errors and fallback to status text.
  }

  return fallback;
}

function appendJsonFormField(
  form: FormData,
  fieldName: string,
  value: Record<string, string> | string[] | undefined,
): void {
  if (value === undefined) {
    return;
  }

  form.append(fieldName, JSON.stringify(value));
}

function buildUploadAssetImportForm(params: UploadAssetImportParams): FormData {
  const form = new FormData();
  form.append('file', params.file);
  form.append('assetTypeId', params.assetTypeId);
  if (params.assetModelId) {
    form.append('assetModelId', params.assetModelId);
  }
  appendJsonFormField(form, 'suggestions', params.suggestions);
  appendJsonFormField(form, 'headers', params.headers);
  appendJsonFormField(form, 'availableFields', params.availableFields);

  return form;
}

export async function uploadAssetImport(
  params: UploadAssetImportParams,
): Promise<UploadAssetResponse> {
  try {
    const apiResponse = await assetApi.organizationAssetControllerUploadRaw(
      {
        organizationId: params.organizationId,
        file: params.file,
        assetTypeId: params.assetTypeId,
        ...(params.assetModelId ? { assetModelId: params.assetModelId } : {}),
        ...(params.suggestions
          ? { suggestions: JSON.stringify(params.suggestions) }
          : {}),
        ...(params.headers ? { headers: JSON.stringify(params.headers) } : {}),
        ...(params.availableFields
          ? { availableFields: JSON.stringify(params.availableFields) }
          : {}),
      },
      assetRequestInit,
    );
    return parseClientJsonResponse(apiResponse, uploadAssetResponseSchema);
  } catch (error) {
    if (error instanceof ResponseError) {
      throw new Error(await safeErrorFromResponse(error.response));
    }
    throw error instanceof Error ? error : new Error('Upload failed');
  }
}

export async function uploadAssetImportWithProgress(params: {
  organizationId: string;
  file: File;
  assetTypeId: string;
  assetModelId?: string;
  suggestions?: Record<string, string>;
  headers?: string[];
  availableFields?: string[];
  onProgress?: (pct: number) => void;
}): Promise<UploadAssetResponse> {
  const form = buildUploadAssetImportForm(params);

  const xhr = new XMLHttpRequest();
  const url = buildApiUrl(
    `/api/v1/organizations/${params.organizationId}/assets/upload`,
  );

  return new Promise<UploadAssetResponse>((resolve, reject) => {
    xhr.open('POST', url, true);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) {
        return;
      }

      const percent = Math.min(
        99,
        Math.round((event.loaded / event.total) * 100),
      );
      params.onProgress?.(percent);
    };

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== XMLHttpRequest.DONE) {
        return;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const parsed = uploadAssetResponseSchema.parse(
            JSON.parse(xhr.responseText) as unknown,
          );
          params.onProgress?.(100);
          resolve(parsed);
        } catch {
          reject(new Error(ERROR_KEYS.PARSE_ERROR));
        }
        return;
      }

      try {
        const payload = JSON.parse(xhr.responseText) as ApiErrorPayload;
        reject(
          new Error(
            extractErrorMessage(payload, `${xhr.status} ${xhr.statusText}`),
          ),
        );
      } catch {
        reject(new Error(`${xhr.status} ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => reject(new Error(ERROR_KEYS.NETWORK_ERROR));
    xhr.onabort = () => reject(new Error(ERROR_KEYS.UPLOAD_ABORTED));
    xhr.send(form);
  });
}

export async function getImportRequests(
  organizationId: string,
): Promise<ImportRequestsResponse> {
  try {
    const apiResponse =
      await assetApi.organizationAssetControllerGetImportRequestsRaw(
        { organizationId },
        assetRequestInit,
      );
    return parseClientJsonResponse(apiResponse, importRequestsResponseSchema);
  } catch (error) {
    return readAssetImportClientError(error, 'Failed to fetch import requests');
  }
}

export async function extractHeaders(
  organizationId: string,
  importRequestId: string,
): Promise<string[]> {
  try {
    const apiResponse =
      await assetApi.organizationAssetControllerExtractHeadersRaw(
        { organizationId, id: importRequestId },
        assetRequestInit,
      );
    return (
      await parseClientJsonResponse(apiResponse, extractHeadersResponseSchema)
    ).headers;
  } catch (error) {
    return readAssetImportClientError(error, 'Failed to extract headers');
  }
}

export async function suggestMappingsByOrganization(params: {
  organizationId: string;
  headers: string[];
}): Promise<{
  suggestions: Record<string, string>;
  availableFields: string[];
  requiredFields: string[];
}> {
  try {
    const apiResponse =
      await assetApi.organizationAssetControllerSuggestMappingsRaw(
        {
          organizationId: params.organizationId,
          organizationAssetControllerSuggestMappingsRequest: {
            headers: params.headers,
          },
        },
        assetRequestInit,
      );
    return parseClientJsonResponse(apiResponse, suggestMappingsResponseSchema);
  } catch (error) {
    return readAssetImportClientError(
      error,
      'Failed to get mapping suggestions',
    );
  }
}

export async function getLastMappingSuggestions(
  organizationId: string,
): Promise<{ suggestions: Record<string, string> }> {
  try {
    const apiResponse = await assetApi.organizationAssetControllerGetMappingRaw(
      { organizationId },
      assetRequestInit,
    );
    return parseClientJsonResponse(
      apiResponse,
      getMappingSuggestionResponseSchema,
    );
  } catch (error) {
    return readAssetImportClientError(
      error,
      'Failed to load saved mapping suggestions',
    );
  }
}
